import React, { memo, useCallback, useMemo, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import { Button, Modal, Popconfirm, Tooltip, message } from 'antd'
import {
  CloseCircleOutlined,
  HomeOutlined,
  ExperimentOutlined,
  WarningOutlined,
  CheckCircleOutlined,
  ArrowLeftOutlined,
  SkinOutlined,
  SmileOutlined,
  CoffeeOutlined,
  CloudOutlined,
  HeartOutlined,
  SafetyCertificateOutlined,
  FormOutlined,
  LoadingOutlined
} from '@ant-design/icons'
import SurveillanceAlertOverlay, {
  formatSurveillanceAlertDuration
} from './SurveillanceAlertOverlay'
import {
  getEventTypeLabel,
  getMonitoringAlertSummary,
  getMonitoringSceneFlags,
  isSevereMonitoringLevel
} from '@/views/commandCenter/profile/ServiceAlerts/monitoringSsePayloadUtils'
import SurveillanceCaptureThumb from './SurveillanceCaptureThumb'
import FaceCapture, { isFaceReadyForDestination } from '@/components/FaceCapture'
import { FACE_SESSION_STATUS } from '../face/faceCaptureSession'
import { useSurveillanceTabletDensity } from '../utils/surveillanceTabletDensity'
import '../List.less'
import './SurveillanceActionPanel.less'

/* ======================================================
 * 监控墙单格状态机 + 操作面板（新设计）
 *
 * 新增 7 种格子呈现态 + 底部固定 2×4 按键面板，
 * 配合推理引擎的 hasBaby 结果做匹配。
 * ====================================================== */

/** 床位业务状态枚举 */
export const CELL_STATE = {
  /** 在位（正常推理中）绿边 */
  IN_PLACE: 'in_place',
  /** 已登记离室，等待推理确认 */
  PRE_DEPARTED: 'pre_departed',
  /** 离室中（A 回房 / B 护理） */
  DEPARTED: 'departed',
  /** 送回待确认，等 Java 再推 in_place */
  RETURN_PENDING: 'return_pending',
  /** 已点送回，画面还没看到宝宝 */
  RETURN_AWAITING: 'return_awaiting',
  /** 推理发现无人 + 无登记 → 红色预警 */
  ALERT: 'alert',
  /** 护理操作进行中（B 专用） */
  NURSING_IN_PROGRESS: 'nursing_in_progress',
  /** 未开推理（不参与在位/无人判定） */
  EMPTY: 'empty'
}

/** 离室类型（与 POST /departurerecord.type 一致；盘面徽章仍用 A/B） */
/**
 * 去向登记 type（POST /departurerecord）与本地离室单 type
 * 护理结束 nextStep 见 {@link NURSING_NEXT_STEP}，勿混用
 */
export const DEPARTURE_TYPE = {
  /** 回客户房间 */
  RETURN_ROOM: 'RETURN_ROOM',
  /** 护理操作 */
  NURSING: 'NURSING'
}

/**
 * 护理结束下一步（POST /departurerecord/nursing/end 的 nextStep）
 * UI 仍用 DEPARTURE_TYPE / 槽位 id，仅在调 nursing/end 时映射到此枚举
 */
export const NURSING_NEXT_STEP = {
  /** 回客户房间（槽位 return_room / DEPARTURE_TYPE.RETURN_ROOM） */
  // RETURN_ROOM: 'RETURN_ROOM',
  RETURN_ROOM: 'return_room',
  /** 继续护理（槽位 type=NURSING） */
  // NEXT_OPERATION: 'NEXT_OPERATION',
  NEXT_OPERATION: 'next_operation',
  /** 回监护室（槽位 return_nursery） */
  // RETURN_NURSERY: 'RETURN_NURSERY'
  RETURN_NURSERY: 'return_nursery'
}

/** 去向盘槽位 → nursing/end nextStep；非护理结束场景勿用 */
export function resolveNursingNextStep (slotTypeOrAction) {
  if (slotTypeOrAction === DEPARTURE_TYPE.NURSING) return NURSING_NEXT_STEP.NEXT_OPERATION
  if (slotTypeOrAction === DEPARTURE_TYPE.RETURN_ROOM || slotTypeOrAction === 'return_room') {
    return NURSING_NEXT_STEP.RETURN_ROOM
  }
  if (slotTypeOrAction === NURSING_NEXT_STEP.RETURN_NURSERY || slotTypeOrAction === 'return_nursery') {
    return NURSING_NEXT_STEP.RETURN_NURSERY
  }
  return ''
}

/** 需刷脸的操作：登记去向 A/B、取消预登记、手动送回/回监护室（nurseId + 抓拍） */
function slotNeedsFaceVerify (slot) {
  if (!slot) return false
  if (slot.id === 'return_room') return true
  if (slot.id === 'cancel_departure') return true
  if (slot.id === 'return_nursery') return true
  return slot.type === DEPARTURE_TYPE.NURSING
}

/** 护理项字典 typeCode（GET /common/dict/options） */
export const NURSING_TYPE_DICT_CODE = 'nursing_type'

/** 已知护理 value → 图标（字典无图标字段；未知 value 用默认图标） */
const NURSING_ICON_BY_VALUE = {
  bath: <SkinOutlined />,
  diaper: <SmileOutlined />,
  feeding: <CoffeeOutlined />,
  swim: <CloudOutlined />,
  soothe: <HeartOutlined />,
  disinfect: <SafetyCertificateOutlined />
}

function resolveDictItemLabel (item) {
  if (!item || typeof item !== 'object') return ''
  const raw = item.label != null ? item.label : (item.desc != null ? item.desc : item.value)
  return String(raw == null ? '' : raw).trim()
}

/** 字典列表 → { [value]: { label } }，供状态文案 / toast */
export function buildNursingLabelMap (list) {
  const map = {}
  if (!Array.isArray(list)) return map
  list.forEach((item) => {
    if (item?.value == null || item.value === '') return
    const id = String(item.value)
    map[id] = { label: resolveDictItemLabel(item) || id }
  })
  return map
}

/** 槽位色 → CSS modifier（IoT 状态色） */
const SLOT_TONE = {
  orange: 'amber',
  blue: 'cyan',
  green: 'green',
  red: 'red',
  violet: 'violet',
  gray: 'slate'
}

/* ===================== Action Panel =================== */

/** 操作面板模式 */
const PANEL_MODE = {
  /** 在位 → 预登记离室 */
  REGISTER_DEPARTURE: 'register_departure',
  /** 红色预警 → 补登记 */
  ALARM_REGISTER: 'alarm_register',
  /** 已预登记，待抱走 / 待推理确认 */
  PRE_DEPARTED: 'pre_departed',
  /** 离室中：同一套去向盘，可改选 */
  RETURN_CONFIRM: 'return_confirm',
  /** B 护理结束 + 下一步 */
  NURSING_END: 'nursing_end',
  /** 送回待确认（等 Java in_place） */
  CONFIRM_RETURN: 'confirm_return',
  /** 已点送回，画面还没看到宝宝 */
  AWAIT_RETURN: 'await_return'
}

/** A：回客户房间（非字典项，固定槽位） */
const RETURN_ROOM_SLOT = {
  id: 'return_room',
  type: DEPARTURE_TYPE.RETURN_ROOM,
  icon: <HomeOutlined />,
  label: '回房间',
  color: 'orange',
  badge: null
}

/**
 * 去向槽位：固定「回房间」+ nursing_type 字典护理项
 * @param {Array<{ value?: string|number, label?: string, desc?: string }>} nursingDictList
 */
function buildDestinationSlots (nursingDictList) {
  const nursingSlots = (Array.isArray(nursingDictList) ? nursingDictList : [])
    .map((item) => {
      if (item?.value == null || item.value === '') return null
      const id = String(item.value)
      return {
        id,
        type: DEPARTURE_TYPE.NURSING,
        icon: NURSING_ICON_BY_VALUE[id] || <SkinOutlined />,
        label: resolveDictItemLabel(item) || id,
        color: 'blue',
        badge: null
      }
    })
    .filter(Boolean)
  return [RETURN_ROOM_SLOT, ...nursingSlots]
}

/** 护理结束：回监护室（须已有离室护理单） */
const RETURN_NURSERY_SLOT = {
  id: 'return_nursery',
  type: null,
  icon: <CheckCircleOutlined />,
  label: '回监护室',
  color: 'green',
  badge: null
}

/** 离室中：手动送回（须已有去向登记；未见宝宝则进待确认） */
const MANUAL_RETURN_SLOT = {
  id: 'return_nursery',
  type: null,
  icon: <CheckCircleOutlined />,
  label: '手动送回',
  color: 'green',
  badge: null
}

/**
 * 底部操作面板，根据当前格子的业务状态决定显示模式。
 * 按键位置不随选项数量变化。
 */
/**
 * 去向操作面板。
 * 业务字段一律从 record 读（含 device 字段 + cellState / departureInfo），
 * 避免父级再逐项拆 props。
 *
 * record 约定：{ ...device, cellState, departureInfo }
 */
function SurveillanceActionPanel ({
  visible,
  onClose,
  record = {},
  destinationSlots,
  onSelectSlot,
  onCancel,
  onFaceVerify,
  onReturnConfirm,
  isTabletDensity = false,
  faceSnapshot = null,
  faceCellKey = '',
  faceRef,
  onFaceChange,
  submitting = false
}) {
  const { cellState, channelNo, bedNo, departureInfo } = record

  const mode = useMemo(() => {
    switch (cellState) {
      case CELL_STATE.IN_PLACE:
        return PANEL_MODE.REGISTER_DEPARTURE
      case CELL_STATE.ALERT:
        return PANEL_MODE.ALARM_REGISTER
      case CELL_STATE.PRE_DEPARTED:
        return PANEL_MODE.PRE_DEPARTED
      case CELL_STATE.RETURN_PENDING:
        return PANEL_MODE.CONFIRM_RETURN
      case CELL_STATE.RETURN_AWAITING:
        return PANEL_MODE.AWAIT_RETURN
      case CELL_STATE.NURSING_IN_PROGRESS:
        return PANEL_MODE.NURSING_END
      case CELL_STATE.DEPARTED:
        return PANEL_MODE.RETURN_CONFIRM
      default:
        return PANEL_MODE.REGISTER_DEPARTURE
    }
  }, [cellState])

  const isPreRegister = mode === PANEL_MODE.REGISTER_DEPARTURE
  const isAlarmRegister = mode === PANEL_MODE.ALARM_REGISTER
  const isPreDeparted = mode === PANEL_MODE.PRE_DEPARTED
  const isNursingEnd = mode === PANEL_MODE.NURSING_END
  /** 离室中：同一套去向盘，可改选 */
  const isDepartedActive = mode === PANEL_MODE.RETURN_CONFIRM
  const isAwaitReturn = mode === PANEL_MODE.AWAIT_RETURN
  /** 有/无婴儿、有/无警告、离室中：去向盘同一套 */
  const isDestinationRegister = isPreRegister
    || isAlarmRegister
    || isNursingEnd
    || isPreDeparted
    || isDepartedActive

  const handleSlotClick = useCallback((slot) => (e) => {
    e.stopPropagation()
    if (typeof onSelectSlot === 'function') {
      onSelectSlot(slot)
    }
  }, [onSelectSlot])

  const faceReady = isFaceReadyForDestination(faceSnapshot)

  const renderGlassSlot = useCallback((slot, { onClick, showBadge = true, disabled = false } = {}) => {
    const tone = SLOT_TONE[slot.color] || 'cyan'
    return (
      <button
        key={slot.id}
        type="button"
        disabled={disabled}
        aria-disabled={disabled}
        className={[
          'surveillance-glass-slot',
          `surveillance-glass-slot--${tone}`,
          disabled ? 'surveillance-glass-slot--disabled' : ''
        ].filter(Boolean).join(' ')}
        onClick={disabled ? undefined : (onClick || handleSlotClick(slot))}
      >
        {showBadge && slot.badge ? (
          <span className="surveillance-glass-slot__badge">{slot.badge}</span>
        ) : null}
        <span className="surveillance-glass-slot__icon">
          {React.isValidElement(slot.icon) ? slot.icon : null}
        </span>
        <span className="surveillance-glass-slot__label">{slot.label}</span>
      </button>
    )
  }, [handleSlotClick])

  const bedLabel = (() => {
    const bed = String(bedNo ?? '').trim()
    if (bed) return bed
    const n = Number(channelNo)
    return Number.isFinite(n) && n > 0 ? n : (channelNo || 1)
  })()

  const panelTitle = useMemo(() => {
    if (isDestinationRegister) {
      return { icon: null, text: '登记去向' }
    }
    if (mode === PANEL_MODE.CONFIRM_RETURN || mode === PANEL_MODE.AWAIT_RETURN) {
      return { icon: <WarningOutlined />, text: '送回待确认' }
    }
    return { icon: null, text: '操作' }
  }, [mode, isDestinationRegister])

  const showChannelRegisterHint = isDestinationRegister

  const registerSlots = useMemo(() => {
    const base = Array.isArray(destinationSlots) ? destinationSlots : [RETURN_ROOM_SLOT]
    // 无人告警 / 在位：只登记去向，无离室单不出现手动送回
    if (isAlarmRegister || isPreRegister) return base
    // 护理中：有离室单 → 回监护室
    if (isNursingEnd) return [...base, RETURN_NURSERY_SLOT]
    // 离室中：有离室单 → 手动送回
    if (isDepartedActive) return [...base, MANUAL_RETURN_SLOT]
    // 预登记：改选去向 + 底栏「取消登记」，不提供手动送回（人还没抱走）
    if (isPreDeparted) return base
    return base
  }, [
    destinationSlots,
    isAlarmRegister,
    isPreRegister,
    isNursingEnd,
    isDepartedActive,
    isPreDeparted
  ])

  /** 送回待确认：紧凑盘 */
  const returnPendingSlots = useMemo(() => ([
    {
      id: 'close_panel',
      type: null,
      icon: <ArrowLeftOutlined />,
      label: '关闭',
      color: 'orange',
      badge: null
    }
  ]), [])

  const compactSlots = useMemo(() => {
    if (mode === PANEL_MODE.CONFIRM_RETURN || mode === PANEL_MODE.AWAIT_RETURN) {
      return returnPendingSlots
    }
    return null
  }, [mode, returnPendingSlots])

  const registerGridCols = (isNursingEnd || isDepartedActive) ? '4' : '3'
  const faceFooter = isDestinationRegister
    ? getFaceAwareFooter(faceSnapshot)
    : { text: DEFAULT_DESTINATION_FOOTER, tone: 'default' }

  return (
    <Modal
      open={visible}
      onCancel={onClose}
      footer={null}
      forceRender
      width={isTabletDensity ? '100%' : 500}
      closable={false}
      maskClosable
      centered={!isTabletDensity}
      wrapClassName={[
        'surveillance-glass-panel-wrap',
        isTabletDensity ? 'surveillance-glass-panel-wrap--sheet' : ''
      ].filter(Boolean).join(' ')}
      styles={{
        mask: { background: 'rgba(0, 0, 0, 0.72)' },
        content: {
          background: 'transparent',
          backgroundColor: 'transparent',
          boxShadow: 'none',
          padding: 0,
          border: 'none',
          borderRadius: isTabletDensity ? '16px 16px 0 0' : 16,
          overflow: 'hidden'
        },
        body: {
          padding: 0,
          background: 'transparent',
          backgroundColor: 'transparent'
        }
      }}
    >
      <div
        className={[
          'surveillance-glass-panel',
          isTabletDensity ? 'surveillance-glass-panel--sheet' : ''
        ].filter(Boolean).join(' ')}
      >
        <div className="surveillance-glass-panel__header">
          <div className="surveillance-glass-panel__title-wrap">
            {panelTitle.icon
              ? React.cloneElement(panelTitle.icon, {
                className: [
                  'surveillance-glass-panel__title-icon',
                  isAlarmRegister ? 'surveillance-glass-panel__title-icon--warn' : ''
                ].filter(Boolean).join(' ')
              })
              : <ExperimentOutlined className="surveillance-glass-panel__title-icon" />}
            <div className="surveillance-glass-panel__title-block">
              <h3 className="surveillance-glass-panel__title">{panelTitle.text}</h3>
              {showChannelRegisterHint ? (
                <p className="surveillance-glass-panel__title-hint">
                  正在登记 {bedLabel} 号床去向
                </p>
              ) : (
                <p className="surveillance-glass-panel__title-hint">
                  {bedLabel} 号床
                </p>
              )}
            </div>
          </div>
          <Button
            type="text"
            className="surveillance-glass-panel__close"
            icon={<CloseCircleOutlined />}
            onClick={onClose}
            aria-label="关闭操作面板"
          />
        </div>

        {compactSlots ? (
          <div className="surveillance-glass-panel__compact">
            <div className="surveillance-glass-panel__grid surveillance-glass-panel__grid--1">
              {compactSlots.map((slot) => renderGlassSlot(slot, {
                onClick: slot.id === 'close_panel' ? onClose : handleSlotClick(slot),
                showBadge: false
              }))}
            </div>
            <p className="surveillance-glass-panel__hint">
              {isAwaitReturn
                ? '画面上还没看到宝宝，等下次看到后再确认送回'
                : '宝宝已经回床了，马上会自动恢复正常'}
            </p>
          </div>
        ) : isDestinationRegister ? (
          <>
            {isNursingEnd && departureInfo?.warning ? (
              <div className="surveillance-glass-panel__alert">
                <WarningOutlined />
                护理超时了，请选择下一步
              </div>
            ) : null}
            <FaceCapture
              ref={faceRef}
              sessionKey={faceCellKey}
              active={visible}
              onChange={onFaceChange}
            />
            <div className={`surveillance-glass-panel__grid surveillance-glass-panel__grid--${registerGridCols}`}>
              {registerSlots.map((slot) => renderGlassSlot(slot, {
                disabled: submitting || (slotNeedsFaceVerify(slot) && !faceReady)
              }))}
            </div>
            <div className="surveillance-glass-panel__footer">
              <span
                className={[
                  'surveillance-glass-panel__footer-text',
                  faceFooter.tone !== 'default'
                    ? `surveillance-glass-panel__footer-text--${faceFooter.tone}`
                    : ''
                ].filter(Boolean).join(' ')}
              >
                {faceFooter.text}
              </span>
              <div className="surveillance-glass-panel__footer-actions">
                {isPreDeparted ? (
                  <Button
                    type="text"
                    className="surveillance-glass-panel__footer-cancel"
                    icon={<CloseCircleOutlined />}
                    disabled={!faceReady}
                    aria-disabled={!faceReady}
                    onClick={faceReady
                      ? handleSlotClick({
                        id: 'cancel_departure',
                        type: null,
                        label: '取消登记'
                      })
                      : undefined}
                  >
                    取消登记
                  </Button>
                ) : null}
                <Button
                  type="text"
                  className="surveillance-glass-panel__footer-cancel"
                  icon={<CloseCircleOutlined />}
                  onClick={onClose}
                >
                  取消
                </Button>
              </div>
            </div>
          </>
        ) : null}
      </div>
    </Modal>
  )
}

/* ============== Cell state → CSS class mapping ============ */

function cellStateToCssClass (cellState) {
  switch (cellState) {
    case CELL_STATE.IN_PLACE:
      return 'surveillance-cell--inference-on'
    case CELL_STATE.PRE_DEPARTED:
      return 'surveillance-cell--pre-departed'
    case CELL_STATE.DEPARTED:
      return 'surveillance-cell--departed'
    case CELL_STATE.RETURN_PENDING:
      return 'surveillance-cell--return-pending'
    case CELL_STATE.RETURN_AWAITING:
      return 'surveillance-cell--return-awaiting'
    case CELL_STATE.ALERT:
      return 'surveillance-cell--alert-pulse'
    case CELL_STATE.NURSING_IN_PROGRESS:
      return 'surveillance-cell--nursing-in-progress'
    case CELL_STATE.EMPTY:
      return 'surveillance-cell--inference-paused'
    default:
      return ''
  }
}

function formatDuration (sinceMs, nowMs) {
  if (!sinceMs) return ''
  const now = nowMs > 0 ? nowMs : Date.now()
  return formatSurveillanceAlertDuration(Math.max(0, now - sinceMs))
}

const DEFAULT_DESTINATION_FOOTER = '选好去向就行，宝宝离开后会自动确认'

/**
 * 去向盘 footer：身份感知文案（无感≠无知）
 * @param {{ status: string, result?: { nurseName?: string }|null }|null} faceSnapshot
 * @returns {{ text: string, tone: 'default'|'recognizing'|'ready'|'failed' }}
 */
function getFaceAwareFooter (faceSnapshot) {
  const status = faceSnapshot?.status
  if (status === FACE_SESSION_STATUS.REQUESTING_PERMISSION) {
    return { text: '请正对屏幕，正在认出你…', tone: 'recognizing' }
  }
  if (status === FACE_SESSION_STATUS.RECOGNIZING) {
    const reason = faceSnapshot?.reason
    if (reason === 'liveness_fail') {
      return {
        text: '请本人正对屏幕，别用照片或手机画面',
        tone: 'recognizing'
      }
    }
    if (reason === 'mismatch' || reason === 'no_face' || reason === 'quality') {
      return {
        text: '还没认出你，请再正对屏幕一下',
        tone: 'recognizing'
      }
    }
    return { text: '请正对屏幕，正在认出你…', tone: 'recognizing' }
  }
  if (status === FACE_SESSION_STATUS.READY) {
    // 姓名只在取景框展示，footer 避免再写一遍「已确认：xxx」
    return {
      text: '选好去向；拍糊了可点取景框重拍',
      tone: 'ready'
    }
  }
  if (status === FACE_SESSION_STATUS.FAILED) {
    const reason = faceSnapshot?.reason
    if (reason === 'permission' || reason === 'device') {
      return {
        text: '打不开摄像头，检查权限后再点取景框重拍',
        tone: 'failed'
      }
    }
    return {
      text: '没拍清脸，点取景框再拍一次',
      tone: 'failed'
    }
  }
  return { text: DEFAULT_DESTINATION_FOOTER, tone: 'default' }
}

function getAlertStatusText (seeData) {
  if (!seeData || typeof seeData !== 'object') return '请登记宝宝去向'
  const summary = getMonitoringAlertSummary(seeData)
  const typeLabel = getEventTypeLabel(seeData)
  const candidate = typeLabel || summary || seeData.subTypeDesc || seeData.alarmName || ''
  const text = String(candidate).trim()
  return text || '请登记宝宝去向'
}

/* ================= Main Component =================== */

/**
 * 监控墙单格组件
 *
 * 单格预览区（内嵌 SurveillanceCaptureThumb），
 * 同时增加业务状态展示 + 底部快捷操作按钮。
 *
 * device：monitoringdevice/list 行原样下传，业务字段直接读 device.xxx，
 * 勿在 List 再拆 deviceSerial / channelNo / name 等。
 */
function SurveillanceCell ({
  device = {},
  index,
  slotIndex,
  isAlarming = false,
  monitoringAlarm = null,
  monitoringBaselineReady,
  latestMonitoringImage = '',
  latestMonitoringImageThumbnail = '',
  deviceIsAlarming = false,
  alarmDisplayEligible = false,
  gridMetrics,

  /* 墙内派生态（非 list 行字段） */
  cellState = CELL_STATE.IN_PLACE,
  departureInfo = null,
  onCellAction,
  isFalsePositive = false,
  wrongCheckPending = false,
  isTabletDensity: isTabletDensityProp,
  /** 父级低频 tick，仅时长格传入非 0，避免整墙跟着重渲染 */
  nowTick = 0,

  /* 子组件（可选覆盖） */
  captureThumb,
  inferenceBadge,
  channelBadge
}) {
  const {
    deviceSerial,
    channelNo,
    source,
    brand,
    name,
    needInference,
    bedNo
  } = device

  const cellRef = useRef(null)
  const tabletDensityFromHook = useSurveillanceTabletDensity()
  const isTabletDensity = isTabletDensityProp == null
    ? tabletDensityFromHook
    : Boolean(isTabletDensityProp)
  const [panelVisible, setPanelVisible] = useState(false)
  const departureSubmitRef = useRef(false)
  const [departureSubmitting, setDepartureSubmitting] = useState(false)
  /** 登记接口发出后才有文案；null 表示还没在请求 */
  const [registeringLabel, setRegisteringLabel] = useState(null)
  const [faceSnapshot, setFaceSnapshot] = useState(null)
  const faceRef = useRef(null)
  const selectGenRef = useRef(0)

  const dictOptions = useSelector((state) => state.components.dictOptions)
  const { nursing_type: nursingTypeDict } = dictOptions
  const nursingLabelMap = useMemo(
    () => buildNursingLabelMap(nursingTypeDict.data),
    [nursingTypeDict.data]
  )
  const destinationSlots = useMemo(
    () => buildDestinationSlots(nursingTypeDict.data),
    [nursingTypeDict.data]
  )

  const cellKey = `${deviceSerial}_${channelNo || 1}`
  const inferenceOn = needInference === true
  const panelRecord = useMemo(() => ({
    ...device,
    cellState,
    departureInfo
  }), [device, cellState, departureInfo])
  const alertHasBaby = getMonitoringSceneFlags(monitoringAlarm?.seeData).hasBaby
  const canOpenPanelByTap = inferenceOn && cellState !== CELL_STATE.EMPTY
  const showAlertOverlay = Boolean(monitoringAlarm)
  // 大床号跟顶栏一起：有 monitoringAlarm 才显示
  const showAlertBedOverlay = showAlertOverlay && cellState === CELL_STATE.ALERT
  const showDepartOverlay = (
    cellState === CELL_STATE.PRE_DEPARTED
    || cellState === CELL_STATE.DEPARTED
    || cellState === CELL_STATE.NURSING_IN_PROGRESS
    || cellState === CELL_STATE.RETURN_AWAITING
  )
  const showBedOverlay = showAlertBedOverlay || showDepartOverlay
  const isNoBabyAlert = Boolean(monitoringAlarm) && alertHasBaby === false
  // 无婴儿固定橙：不跟 list「高/特急」抢红紫，远距一眼能分
  const alertLevelColor = isNoBabyAlert
    ? 'orange'
    : (String(monitoringAlarm?.levelCfg?.color || '').trim() || (showAlertOverlay ? 'red' : ''))
  const alertLevelValue = Number(monitoringAlarm?.levelValue) || 0
  // 红框跟顶栏同一条件：有 monitoringAlarm 才闪；ALERT 但无载荷时禁止再套 alert-pulse（空红框）
  const showAlertChrome = showAlertOverlay
  const flashHighAlert = showAlertChrome
    && !isNoBabyAlert
    && (
      alertLevelValue >= 3
      || Boolean(monitoringAlarm?.seeData && isSevereMonitoringLevel(monitoringAlarm.seeData))
    )
  const cellCssClass = (() => {
    if (!showAlertChrome) {
      if (cellState === CELL_STATE.ALERT) return ''
      return cellStateToCssClass(cellState)
    }
    const color = alertLevelColor || 'red'
    return [
      'surveillance-cell--alert-pulse',
      `surveillance-cell--alert-${color}`,
      flashHighAlert ? 'surveillance-cell--alert-flash' : ''
    ].filter(Boolean).join(' ')
  })()

  const openDeparturePanel = useCallback(() => {
    if (departureSubmitRef.current) return
    // 送回待确认紧凑盘不刷脸；其余去向盘与点击同栈开流（QQ/微信授权）
    const needsFace = cellState !== CELL_STATE.RETURN_PENDING
      && cellState !== CELL_STATE.RETURN_AWAITING
      && cellState !== CELL_STATE.EMPTY
    if (needsFace && faceRef.current) faceRef.current.start()
    setPanelVisible(true)
  }, [cellState])

  const handlePreviewClick = useCallback((e) => {
    if (!canOpenPanelByTap) return
    const target = e.target
    if (
      target &&
      typeof target.closest === 'function' &&
      target.closest('button, a, .ant-btn, input, textarea, .ant-switch')
    ) {
      return
    }
    openDeparturePanel()
  }, [canOpenPanelByTap, openDeparturePanel])

  const emitCellAction = useCallback((slot, faceResult) => {
    if (typeof onCellAction !== 'function') return undefined
    return onCellAction({
      action: slot.id,
      type: slot.type,
      cellState,
      departureInfo,
      deviceSerial,
      channelNo,
      nurseId: faceResult?.nurseId,
      nurseName: faceResult?.nurseName,
      nurseAvatar: faceResult?.nurseAvatar || null,
      mockFace: Boolean(faceResult?.mock)
    })
  }, [onCellAction, cellState, departureInfo, deviceSerial, channelNo])

  /** 用户关盘：取消识别并停流（选槽关盘不走这里，避免杀掉合流中的会话） */
  const handleClosePanel = useCallback(() => {
    if (faceRef.current) faceRef.current.stop()
    setPanelVisible(false)
  }, [])

  const handleSelectSlot = useCallback(async (slot) => {
    if (departureSubmitRef.current) return

    if (slotNeedsFaceVerify(slot) && !isFaceReadyForDestination(faceSnapshot)) {
      // 盘上按钮已禁用；此处防竞态 / 非 UI 调用
      return
    }

    const gen = ++selectGenRef.current
    departureSubmitRef.current = true
    setDepartureSubmitting(true)
    setPanelVisible(false)

    const release = () => {
      if (gen !== selectGenRef.current) return
      departureSubmitRef.current = false
      setDepartureSubmitting(false)
      setRegisteringLabel(null)
    }

    const waitDepartureRequest = async (pending) => {
      if (!pending || typeof pending.then !== 'function') return
      const name = slot.label != null ? String(slot.label).trim() : ''
      setRegisteringLabel(name)
      await pending
    }

    try {
      if (!slotNeedsFaceVerify(slot)) {
        if (faceRef.current) faceRef.current.stop()
        await waitDepartureRequest(emitCellAction(slot, null))
        return
      }

      const faceResult = faceRef.current
        ? await faceRef.current.awaitResult()
        : { ok: false }
      if (gen !== selectGenRef.current) return
      const hasCapture = faceResult?.ok
        && faceResult.nurseAvatar
        && typeof Blob !== 'undefined'
        && faceResult.nurseAvatar instanceof Blob
      if (hasCapture) {
        if (faceRef.current) faceRef.current.stop()
        await waitDepartureRequest(emitCellAction(slot, faceResult))
        return
      }
      // 没拍到 / 没拍清 / 识别失败 → 重新开流重拍（不走 mock 兜底）
      message.warning('没拍清脸，请正对摄像头再拍一次')
      setPanelVisible(true)
      if (faceRef.current) faceRef.current.start()
    } finally {
      release()
    }
  }, [emitCellAction, faceSnapshot])

  return (
    <>
      <div
        ref={cellRef}
        key={cellKey}
        className="surveillance-grid-item"
      >
        <div
          className={[
            'surveillance-cell',
            cellCssClass,
            !inferenceOn ? 'surveillance-cell--inference-paused' : '',
            canOpenPanelByTap ? 'surveillance-cell--tappable' : ''
          ]
            .filter(Boolean)
            .join(' ')}
          style={
            gridMetrics?.cellHeight > 0
              ? { height: `${gridMetrics.cellHeight}px` }
              : undefined
          }
        >
          {/* 预览区：点击打开登记/操作面板（避开内部按钮） */}
          <div
            className="surveillance-cell__preview"
            role={canOpenPanelByTap ? 'button' : undefined}
            tabIndex={canOpenPanelByTap ? 0 : undefined}
            aria-label={canOpenPanelByTap ? '打开去向登记' : undefined}
            onClick={handlePreviewClick}
            onKeyDown={canOpenPanelByTap ? (e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault()
                openDeparturePanel()
              }
            } : undefined}
            style={
              gridMetrics?.previewHeight > 0
                ? { height: `${gridMetrics.previewHeight}px` }
                : undefined
            }
          >
            {/* 预警横幅：独占顶栏，不与床号/错检叠层抢位 */}
            {showAlertOverlay ? (
              <SurveillanceAlertOverlay
                badgeTitle={isNoBabyAlert ? '无婴儿' : '异常预警'}
                levelText={
                  isNoBabyAlert
                    ? '看护'
                    : (monitoringAlarm.levelText || monitoringAlarm.levelCfg?.text || '预警')
                }
                levelColor={alertLevelColor || 'red'}
                statusText={
                  isNoBabyAlert
                    ? (isTabletDensity ? '请登记去向' : '请登记宝宝去向')
                    : getAlertStatusText(monitoringAlarm.seeData)
                }
                eventTimeMs={monitoringAlarm.eventTimeMs}
                durationMs={
                  monitoringAlarm.eventTimeMs
                    ? Math.max(0, (nowTick || Date.now()) - monitoringAlarm.eventTimeMs)
                    : 0
                }
                severe={
                  !isNoBabyAlert
                  && monitoringAlarm.seeData
                  && isSevereMonitoringLevel(monitoringAlarm.seeData)
                }
                flash={flashHighAlert}
                compact={isTabletDensity}
              />
            ) : null}

            {/* 送回待确认：已点手动送回，等推理看到宝宝（空档内继续计时） */}
            {cellState === CELL_STATE.RETURN_AWAITING && (
              <div
                className="surveillance-alert-overlay surveillance-alert-overlay--orange"
                style={{ position: 'relative', flexShrink: 0, zIndex: 5 }}
              >
                <div className="surveillance-alert-overlay__status-row">
                  <span className="surveillance-alert-overlay__badge">
                    <WarningOutlined className="surveillance-alert-overlay__badge-ico" />
                    待确认
                  </span>
                  <span className="surveillance-alert-overlay__type">
                    已点送回，等画面看到宝宝；超时将恢复登记
                  </span>
                </div>
              </div>
            )}

            {/* 送回待确认：推理已匹配有婴，等 Java 再推 in_place */}
            {cellState === CELL_STATE.RETURN_PENDING && (
              <div
                className="surveillance-alert-overlay surveillance-alert-overlay--orange"
                style={{ position: 'relative', flexShrink: 0, zIndex: 5 }}
              >
                <div className="surveillance-alert-overlay__status-row">
                  <span className="surveillance-alert-overlay__badge">
                    <WarningOutlined className="surveillance-alert-overlay__badge-ico" />
                    待确认
                  </span>
                  <span className="surveillance-alert-overlay__type">
                    画面已看到宝宝，等待系统确认回在位
                  </span>
                </div>
              </div>
            )}

            {/* 画面层：床号/错检/截图只在此区域内叠，避免盖住顶栏预警信息 */}
            <div className="surveillance-cell__stage">
              {/* 错检：仅有详细预警横幅时展示（与红框同条件，避免关单后空红框仍出错检） */}
              {needInference && cellState !== CELL_STATE.EMPTY && showAlertOverlay && !isFalsePositive && (
                <div
                  className={[
                    'surveillance-cell__wrong-check-wrap',
                    showBedOverlay
                      ? 'surveillance-cell__wrong-check-wrap--with-bed'
                      : (showAlertOverlay ? 'surveillance-cell__wrong-check-wrap--below-alert' : '')
                  ].filter(Boolean).join(' ')}
                  onClick={(e) => e.stopPropagation()}
                  role="presentation"
                >
                  <Popconfirm
                    title="确定是识别错了？"
                    okText="确认看错了"
                    cancelText="取消"
                    placement={
                      showBedOverlay
                        ? (isTabletDensity ? 'bottomRight' : 'topLeft')
                        : 'bottomLeft'
                    }
                    onConfirm={() => {
                      if (wrongCheckPending) return
                      emitCellAction({ id: 'wrong_check', type: null }, null)
                    }}
                  >
                    <Button
                      type="text"
                      size="small"
                      icon={<CloseCircleOutlined />}
                      className="surveillance-cell__wrong-check"
                      aria-label="错检"
                      disabled={wrongCheckPending}
                    >
                      {isTabletDensity && showBedOverlay ? null : '错检'}
                    </Button>
                  </Popconfirm>
                </div>
              )}

              {/* 预警床位号叠层：无人补登记；大号通道号供远距扫视找床 */}
              {showAlertBedOverlay && (
                <div className="surveillance-cell__alert-bed-overlay">
                  <div className="surveillance-cell__alert-bed-overlay-inner">
                    {!isTabletDensity ? (
                      <span className="surveillance-cell__alert-bed-label">床位</span>
                    ) : null}
                    <span className="surveillance-cell__alert-bed-num">{bedNo || ''}</span>
                  </div>
                </div>
              )}

              {/* 琥珀色去向态：床号 + 去向，与预警床号同级居中大号 */}
              {showDepartOverlay && (
                <div className="surveillance-cell__depart-overlay">
                  <div className="surveillance-cell__depart-overlay-inner">
                    <span className="surveillance-cell__depart-overlay-label">床位</span>
                    <span className="surveillance-cell__depart-overlay-bed">
                      {String(bedNo ?? '').trim() || channelNo || 1}
                    </span>
                    <span className="surveillance-cell__depart-overlay-dest">
                      {departureInfo?.type === DEPARTURE_TYPE.RETURN_ROOM
                        ? '回房间'
                        : (
                          (departureInfo?.subType != null
                            && nursingLabelMap[String(departureInfo.subType)]?.label)
                          || '护理'
                        )}
                    </span>
                    {departureInfo?.nurseName ? (
                      <span className="surveillance-cell__depart-overlay-meta">
                        带走：{departureInfo.nurseName}
                      </span>
                    ) : null}
                  </div>
                  {(
                    cellState === CELL_STATE.DEPARTED
                    || cellState === CELL_STATE.NURSING_IN_PROGRESS
                    || cellState === CELL_STATE.RETURN_AWAITING
                  ) && departureInfo?.departedAt ? (
                    <span className="surveillance-cell__depart-overlay-meta surveillance-cell__depart-overlay-meta--duration">
                      {departureInfo?.type === DEPARTURE_TYPE.RETURN_ROOM ? '离室' : '计时'}
                      {' '}
                      {formatDuration(departureInfo.departedAt, nowTick)}
                    </span>
                  ) : null}
                </div>
              )}

              {registeringLabel != null ? (
                <div className="surveillance-cell__registering" role="status">
                  <div className="surveillance-cell__registering-plate">
                    <LoadingOutlined className="surveillance-cell__registering-icon" />
                    <span className="surveillance-cell__registering-title">正在登记</span>
                    {registeringLabel ? (
                      <span className="surveillance-cell__registering-dest">{registeringLabel}</span>
                    ) : null}
                  </div>
                </div>
              ) : null}

              {captureThumb || (
                <SurveillanceCaptureThumb
                  deviceSerial={deviceSerial}
                  channelNo={channelNo ?? 0}
                  source={source}
                  brand={brand}
                  status={device.status}
                  monitoringAlarm={monitoringAlarm}
                  monitoringBaselineReady={monitoringBaselineReady}
                  deviceLatestMonitoringImage={latestMonitoringImage}
                  deviceLatestMonitoringImageThumbnail={latestMonitoringImageThumbnail}
                  deviceIsAlarming={deviceIsAlarming || isAlarming}
                  alarmDisplayEligible={alarmDisplayEligible || inferenceOn}
                />
              )}
            </div>
          </div>

          {/* 底栏：在线态 + 设备名 + 通道号 + 去向主操作 */}
          <div className="surveillance-cell__label">
            {cellState === CELL_STATE.IN_PLACE && !showAlertOverlay && isFalsePositive ? (
              <span
                className="surveillance-cell__label-status surveillance-cell__label-status--false-positive"
                title="已标记识别有误，按在位显示"
              >
                已点错检
              </span>
            ) : null}
            {(device.status === 1 || device.status === '1' || device.status === 0 || device.status === '0') ? (
              <span
                className={
                  device.status === 1 || device.status === '1'
                    ? 'surveillance-cell__label-status surveillance-cell__label-status--online'
                    : 'surveillance-cell__label-status surveillance-cell__label-status--offline'
                }
              >
                {device.status === 1 || device.status === '1' ? '在线' : '离线'}
              </span>
            ) : null}

            <span className="surveillance-cell__label-name">
              {name || deviceSerial || `设备${(slotIndex || 0) + 1}`}
            </span>

            {channelBadge || (
              <div
                className={[
                  'surveillance-cell__channel-badge',
                  cellState === CELL_STATE.ALERT ? 'surveillance-cell__channel-badge--alert' : '',
                  cellState === CELL_STATE.ALERT && alertLevelColor
                    ? `surveillance-cell__channel-badge--alert-${alertLevelColor}`
                    : ''
                ].filter(Boolean).join(' ')}
                title={`摄像头通道 ${channelNo || 1}`}
              >
                <span className="surveillance-cell__channel-badge-kicker">通道</span>
                <span className="surveillance-cell__channel-badge-no">
                  {channelNo || 1}
                </span>
              </div>
            )}
            {needInference && cellState !== CELL_STATE.EMPTY && (
              <span className="surveillance-cell__action-wrap">
                <Tooltip title={registeringLabel != null ? '正在登记' : '登记去向'} mouseEnterDelay={0.3}>
                  <Button
                    type="text"
                    size="small"
                    icon={registeringLabel != null ? <LoadingOutlined /> : <FormOutlined />}
                    className={[
                      'surveillance-cell__action-btn',
                      registeringLabel != null ? 'surveillance-cell__action-btn--registering' : ''
                    ].filter(Boolean).join(' ')}
                    aria-label={registeringLabel != null ? '正在登记' : '去向'}
                    disabled={departureSubmitting}
                    onClick={(e) => {
                      e.stopPropagation()
                      if (departureSubmitRef.current) return
                      openDeparturePanel()
                    }}
                  >
                    {registeringLabel != null ? '正在登记' : '去向'}
                  </Button>
                </Tooltip>
              </span>
            )}
          </div>
        </div>
      </div>

      {/* 操作面板 */}
      <SurveillanceActionPanel
        visible={panelVisible}
        onClose={handleClosePanel}
        record={panelRecord}
        destinationSlots={destinationSlots}
        onSelectSlot={handleSelectSlot}
        onCancel={handleClosePanel}
        isTabletDensity={isTabletDensity}
        faceSnapshot={faceSnapshot}
        faceCellKey={cellKey}
        faceRef={faceRef}
        onFaceChange={setFaceSnapshot}
        submitting={departureSubmitting}
      />
    </>
  )
}

export default memo(SurveillanceCell)

export { SurveillanceActionPanel }
